export class CrawledPagesLayout {
	constructor(i18n, containerNode) {
		this.__ = i18n;
		this.containerNode = containerNode;
		this.layoutDetection = null;
	}

	setLayoutDetection(layoutDetection) {
		this.layoutDetection = layoutDetection && typeof layoutDetection === 'object' ? layoutDetection : null;
	}

	isReady() {
		return Boolean(this.containerNode);
	}

	bindPageUrlOpen(onPageUrlOpen) {
		this.containerNode.addEventListener('click', (event) => {
			const pageLink = event.target.closest('a[data-crawled-page-index]');

			if (!pageLink) {
				return;
			}

			event.preventDefault();
			const pageIndex = Number.parseInt(pageLink.dataset.crawledPageIndex || '', 10);

			if (Number.isNaN(pageIndex)) {
				return;
			}

			onPageUrlOpen(pageIndex);
		});
	}

	render(pages) {
		if (pages.length === 0) {
			this.containerNode.classList.remove('has-list-table');
			this.containerNode.textContent = this.__( 'No pages crawled yet.', 'a11yfix' );
			return;
		}

		const orderedPages = pages
			.map((page, originalIndex) => ({ page, originalIndex }))
			.sort((leftItem, rightItem) => {
				const leftTime = typeof leftItem.page?.crawledAt === 'string'
					? new Date(leftItem.page.crawledAt).getTime()
					: Number.NaN;
				const rightTime = typeof rightItem.page?.crawledAt === 'string'
					? new Date(rightItem.page.crawledAt).getTime()
					: Number.NaN;

				if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
					return rightTime - leftTime;
				}

				if (Number.isFinite(leftTime) !== Number.isFinite(rightTime)) {
					return Number.isFinite(rightTime) ? 1 : -1;
				}

				return rightItem.originalIndex - leftItem.originalIndex;
			});

		this.containerNode.classList.add('has-list-table');
		this.containerNode.innerHTML = '';

		const summary = document.createElement('p');
		const latestPageWithDate = orderedPages.find((item) => typeof item.page?.crawledAt === 'string' && item.page.crawledAt.length > 0);
		const latestDate = latestPageWithDate ? this.formatDateTime(latestPageWithDate.page.crawledAt) : this.__( 'N/A', 'a11yfix' );
		summary.textContent = `${this.__( 'Stored pages:', 'a11yfix' )} ${pages.length} · ${this.__( 'Last crawl:', 'a11yfix' )} ${latestDate}`;
		this.containerNode.appendChild(summary);
		this.containerNode.appendChild(this.createLayoutDetectionPanel());

		const table = document.createElement('table');
		table.className = 'a11yfix-results-table a11yfix-crawl-table widefat fixed striped table-view-list';
		const head = document.createElement('thead');
		const headRow = document.createElement('tr');
		const headUrl = document.createElement('th');
		const headDepth = document.createElement('th');
		const headPostId = document.createElement('th');
			const headCrawledAt = document.createElement('th');

		headUrl.textContent = this.__( 'URL', 'a11yfix' );
		headDepth.textContent = this.__( 'Depth', 'a11yfix' );
		headPostId.textContent = this.__( 'ID', 'a11yfix' );
			headCrawledAt.textContent = this.__( 'Crawled date and time', 'a11yfix' );
		headRow.appendChild(headUrl);
		headRow.appendChild(headDepth);
		headRow.appendChild(headPostId);
			headRow.appendChild(headCrawledAt);
		head.appendChild(headRow);
		table.appendChild(head);

		const body = document.createElement('tbody');

		for (const { page, originalIndex } of orderedPages) {
			const row = document.createElement('tr');
			const urlCell = document.createElement('td');
			const depthCell = document.createElement('td');
			const postIdCell = document.createElement('td');
			const crawledAtCell = document.createElement('td');
			const link = document.createElement('a');

			link.href = page.url;
			link.dataset.crawledPageIndex = String(originalIndex);
			link.textContent = page.url;

			depthCell.textContent = String(page.depth);
			postIdCell.textContent = this.getPageIdDisplayValue(page);
			crawledAtCell.textContent = this.formatDateTime(page.crawledAt);
			urlCell.appendChild(link);
			row.appendChild(urlCell);
			row.appendChild(depthCell);
			row.appendChild(postIdCell);
			row.appendChild(crawledAtCell);
			body.appendChild(row);
		}

		table.appendChild(body);
		this.containerNode.appendChild(table);
	}

	createLayoutDetectionPanel() {
		const panel = document.createElement('div');
		panel.className = 'a11yfix-layout-detection';

		const title = document.createElement('p');
		title.className = 'a11yfix-layout-detection-title';
		title.textContent = this.__( 'Common layout detection', 'a11yfix' );
		panel.appendChild(title);

		const list = document.createElement('ul');
		list.className = 'a11yfix-layout-detection-list';

		list.appendChild(this.createLayoutDetectionItem(this.__( 'Header', 'a11yfix' ), this.layoutDetection?.header));
		list.appendChild(this.createLayoutDetectionItem(this.__( 'Footer', 'a11yfix' ), this.layoutDetection?.footer));

		panel.appendChild(list);
		return panel;
	}

	createLayoutDetectionItem(label, detectionPart) {
		const item = document.createElement('li');
		const detection = detectionPart && typeof detectionPart === 'object' ? detectionPart : {};

		const selectorText = detection.detected && detection.selector
			? detection.selector
			: this.__( 'Not detected', 'a11yfix' );
		const confidenceText = `${Math.round((detection.confidence || 0) * 100)}%`;
		const coverageText = `${detection.matchedPages || 0}/${detection.consideredPages || 0}`;

		item.textContent = `${label}: ${selectorText} · ${this.__( 'Confidence', 'a11yfix' )}: ${confidenceText} · ${this.__( 'Coverage', 'a11yfix' )}: ${coverageText}`;
		return item;
	}

	formatDateTime(value) {
		if (typeof value !== 'string' || value.length === 0) {
			return this.__( 'N/A', 'a11yfix' );
		}

		const date = new Date(value);

		if (Number.isNaN(date.getTime())) {
			return this.__( 'N/A', 'a11yfix' );
		}

		return date.toLocaleString();
	}

	getPageIdDisplayValue(page) {
		const pageType = String(page?.pageType || '').toLowerCase();

		if (pageType === 'home' || pageType === 'archive') {
			return pageType;
		}

		return page?.postId ? String(page.postId) : this.__( 'N/A', 'a11yfix' );
	}
}
